<template>
    <div class="container mt-4">
        <div class="page-header">
            <div class="page-header__titles">
                <span class="page-header__accent"></span>
                <div>
                    <h2 class="page-header__title">All Reviews</h2>
                    <p class="page-header__subtitle">
                        Showing reported reviews first — search or change the status filter to see everything.
                    </p>
                </div>
            </div>
        </div>

        <FiltersForm v-model="filters" :show-toggles="false" @apply="onApply" @reset="refresh">
            <div class="filters__field">
                <label class="filters__label">Supplier</label>
                <div class="filters__select-wrap">
                    <select v-model="filters.supplier_id" class="filters__select">
                        <option value="">All Suppliers</option>
                        <option v-for="supplier in supplierOptions" :key="supplier.id" :value="supplier.id">
                            {{ supplier.name }}
                        </option>
                    </select>
                </div>
            </div>

            <!-- Status -->
            <div class="filters__field">
                <label class="filters__label">Status</label>
                <div class="filters__select-wrap">
                    <select v-model="filters.status" class="filters__select">
                        <option value="reported">Reported</option>
                        <!-- <option value="pending">Pending</option> -->
                        <!-- <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option> -->
                        <option value="all">All Reviews</option>
                    </select>
                </div>
            </div>
        </FiltersForm>

        <div class="card p-1 bg-white">
            <LoadingBar v-if="loading" message="Loading Reviews..." />

            <div v-else class="table-wrapper">
                <ReusableTable :rows="reviews" row-key="id" :selectable="false" :actions="true"
                    empty-text="No reviews found">

                    <template #header>
                        <th style="width: 20%">Supplier Reviewed</th>
                        <th style="width: 20%">Reviewer</th>
                        <th style="width: 10%">Rating</th>
                        <th style="width: 40%">Review</th>
                        <th style="width: 15%">Date</th>
                    </template>
                    <template #row="{ row: review }">
                        <td class="align-middle">
                            <div class="d-flex align-items-center gap-2">
                                <div class="d-flex align-items-center justify-content-center bg-primary-subtle border border-primary-subtle rounded-3"
                                    style="width: 30px; height: 30px; min-width: 30px;">
                                    <span class="material-symbols-outlined text-primary fs-10">domain</span>
                                </div>
                                <span class="text-dark">{{ personName(review.supplier) }}</span>
                            </div>
                        </td>
                        <td>
                            <span class="text-dark">{{ personName(review.reviewer) }}</span>
                        </td>
                        <td>
                            <span class="review-stars">
                                <span v-for="n in 5" :key="n" class="review-star"
                                    :class="{ 'review-star--filled': n <= review.stars }">★</span>
                            </span>
                        </td>
                        <td>
                            <span class="review-snippet">{{ truncate(review.review || review.comments || '—', 90)
                                }}</span>
                        </td>
                        <td class="text-muted fs-12">{{ formatDate(review.created_at) }}</td>
                      
                    </template>

                    <template #menu="{ row: review, close }">
                        <li>
                            <router-link v-if="review.status === 'reported'"
                                :to="{ name: 'ReviewDetail', params: { id: review.id } }"
                                class="dropdown-item">
                                <i class="fa fa-flag"></i> View Report
                            </router-link>
                        </li>
                        <li>
                            <a class="dropdown-item" @click.prevent="openDetailModal(review); close()">
                                <i class="fa fa-eye text-info"></i>
                                <span>View Details</span>
                            </a>
                        </li>
                        <hr class="dropdown-divider" />
                        <li>
                            <a class="dropdown-item text-danger" @click.prevent="confirmDeleteReview(review); close()">
                                <i class="fa fa-trash text-danger"></i>
                                <span>Delete</span>
                            </a>
                        </li>
                    </template>
                </ReusableTable>
            </div>

            <Pagination :pagination="pagination" @page-change="changePage" />
        </div>
<ConfirmDeleteModal
  v-model="showDeleteModal"
  title="Delete Review"
  :item-name="deletingReview ? `review by ${personName(deletingReview.reviewer)}` : ''"
  :loading="deleting"
  @confirm="deleteReview"
/>
        <!-- ============ Review detail modal ============ -->
        <FormModal ref="detailModal" title="Review Details" id="review_detail_modal">
            <div v-if="viewingReview">
                <div class="review-detail__row">
                    <span class="review-detail__label">Supplier Reviewed</span>
                    <strong>{{ personName(viewingReview.supplier) }}</strong>
                </div>
                <div class="review-detail__row">
                    <span class="review-detail__label">Reviewer</span>
                    <strong>{{ personName(viewingReview.reviewer) }}</strong>
                </div>
                <div class="review-detail__row">
                    <span class="review-detail__label">Rating</span>
                    <span class="review-stars">
                        <span v-for="n in 5" :key="n" class="review-star"
                            :class="{ 'review-star--filled': n <= viewingReview.stars }">★</span>
                    </span>
                </div>
                <div class="review-detail__row">
                    <span class="review-detail__label">Status</span>
                    <span class="review-status-badge" :class="`review-status-badge--${viewingReview.status}`">
                        {{ formatStatus(viewingReview.status) }}
                    </span>
                </div>

                <div v-if="viewingReview.review" class="review-detail__block">
                    <span class="review-detail__label">Review</span>
                    <p>{{ viewingReview.review }}</p>
                </div>
                <div v-if="viewingReview.comments" class="review-detail__block">
                    <span class="review-detail__label">Additional Comments</span>
                    <p>{{ viewingReview.comments }}</p>
                </div>

                <template v-if="viewingReview.status === 'reported'">
                    <div class="review-detail__block review-detail__block--flagged">
                        <span class="review-detail__label">
                            <i class="fa fa-flag text-danger"></i> Reported By
                        </span>
                        <strong>{{ personName(viewingReview.reporter) }}</strong>
                        <span v-if="viewingReview.reported_at" class="text-muted fs-12 d-block mt-1">
                            {{ formatDate(viewingReview.reported_at) }}
                        </span>
                    </div>
                    <div v-if="viewingReview.report_reason" class="review-detail__block review-detail__block--flagged">
                        <span class="review-detail__label">Report Reason</span>
                        <p>{{ viewingReview.report_reason }}</p>
                    </div>
                </template>

                <div class="rform__row" style="justify-content: flex-end; margin-top: 16px;">
                    <button type="button" class="rform__btn rform__btn--ghost" @click="closeDetailModal">Close</button>
                </div>
            </div>
        </FormModal>
    </div>
</template>

<script>
import Pagination from '@/components/utils/PaginationBar.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import ReusableTable from '@/components/utils/ListTable.vue'
import FiltersForm from '@/components/utils/FiltersForm.vue'
import FormModal from '@/components/utils/FormModal.vue'
import { form_api_submit } from '@/utils/FormSubmit'
import ConfirmDeleteModal from '@/components/utils/ConfirmDeleteModal.vue'
export default {
    name: 'AllReviews',
    components: {
        Pagination,
        LoadingBar,
        ReusableTable,
        FiltersForm,
        FormModal,
        ConfirmDeleteModal,
    },

    data() {
        return {
            reviews: [],
            loading: false,
            supplierOptions: [],

            filters: {
                supplier: '',
                supplier_id: '',
                status: 'reported',
            },
            currentFilters: { status: 'reported' },

            pagination: {
                current_page: 1,
                per_page: 20,
                total_rows: 0,
                total_pages: 1,
            },

            viewingReview: null,
            processingId: null,
             showDeleteModal: false,
            deletingReview: null,
            deleting: false,
        }
    },

    mounted() {
        this.fetchList()
    },

    methods: {
        personName(person) {
            if (!person) return '—'
            return person.company_name || [person.first_name, person.last_name].filter(Boolean).join(' ') || '—'
        },

        truncate(str, len) {
            if (!str) return ''
            return str.length > len ? str.slice(0, len) + '…' : str
        },

        formatStatus(status) {
            const map = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', reported: 'Reported' }
            return map[status] || status || 'Pending'
        },

        formatDate(value) {
            if (!value) return '—'
            const d = new Date(value)
            if (Number.isNaN(d.getTime())) return value
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        },

        async fetchList() {
            this.loading = true

            const result = await form_api_submit({
                url: 'reviews/',
                method: 'GET',
                data: {
                    page: this.pagination.current_page,
                    status: this.currentFilters.status && this.currentFilters.status !== 'all' ? this.currentFilters.status : undefined,
                    supplier: this.currentFilters.supplier || undefined,
                    supplier_id: this.currentFilters.supplier_id || undefined,
                },
                show_success_toast: false,
                show_error_toast: true,
            })

            if (result.success) {
                const payload = result.data
                const reviewsData = payload.reviews || payload
                const rows = Array.isArray(reviewsData) ? reviewsData : reviewsData?.data || []

                this.reviews = rows

                // Set suppliers from the same response
                if (payload.suppliers) {
                    this.supplierOptions = payload.suppliers
                }

                if (!Array.isArray(reviewsData)) {
                    this.pagination = {
                        current_page: reviewsData.current_page ?? 1,
                        per_page: reviewsData.per_page ?? 20,
                        total_rows: reviewsData.total ?? rows.length,
                        total_pages: reviewsData.last_page ?? 1,
                    }
                } else {
                    this.pagination.total_rows = rows.length
                    this.pagination.total_pages = 1
                }
            }

            this.loading = false
        },

        onApply(filters) {
            this.currentFilters = { ...filters }
            this.pagination.current_page = 1
            this.fetchList()
        },

        refresh() {
            this.filters = { supplier: '', supplier_id: '', status: 'reported' }
            this.currentFilters = { status: 'reported' }
            this.pagination.current_page = 1
            this.fetchList()
        },

        changePage(page) {
            if (page < 1 || page > this.pagination.total_pages) return
            this.pagination.current_page = page
            this.fetchList()
        },

        openDetailModal(review) {
            this.viewingReview = review
            this.$refs.detailModal.showModal()
        },

        closeDetailModal() {
            this.$refs.detailModal.closeModal()
            this.viewingReview = null
        },
        async approveReview(review) {
            this.processingId = review.id
            const result = await form_api_submit({
                url: `reviews/${review.id}/approve`,
                method: 'POST',
                show_success_toast: true,
                show_error_toast: true,
            })
            this.processingId = null
            if (result.success) {
                review.status = 'approved'
            }
        },
        async rejectReview(review) {
            this.processingId = review.id
            const result = await form_api_submit({
                url: `reviews/${review.id}/reject`,
                method: 'POST',
                show_success_toast: true,
                show_error_toast: true,
            })
            this.processingId = null
            if (result.success) {
                review.status = 'rejected'
            }
        },
         confirmDeleteReview(review) {
            this.deletingReview = review
            this.showDeleteModal = true
        },
        async deleteReview() {
            this.deleting = true
            const result = await form_api_submit({
            url: `reviews/delete/${this.deletingReview.id}`,
            method: 'DELETE',
            show_success_toast: true,
            show_error_toast: true,
            })
            this.deleting = false
            this.showDeleteModal = false
            if (result.success) {
            this.reviews = this.reviews.filter((r) => r.id !== this.deletingReview.id)
            }
            this.deletingReview = null
        },
    },
}
</script>

<style scoped>
.table-wrapper table {
    width: 100%;
    table-layout: fixed;
}

.review-stars {
    display: inline-flex;
    gap: 1px;
}

.review-star {
    color: var(--color-border, #e2e2e2);
    font-size: 14px;
}

.review-star--filled {
    color: #ec993a;
}

.review-snippet {
    color: var(--color-text-dark);
    font-size: 13px;
}


.review-detail__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid var(--color-border);
    font-size: 13.5px;
}

.review-detail__label {
    color: var(--color-text-muted);
    font-weight: 600;
    font-size: 12px;
}

.review-detail__block {
    margin-top: 14px;
}

.review-detail__block p {
    margin: 6px 0 0;
    font-size: 13.5px;
    color: var(--color-text-dark);
    line-height: 1.5;
}

.review-detail__block--flagged {
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(179, 38, 30, 0.05);
    border: 1px solid rgba(179, 38, 30, 0.18);
}

.btn-review {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    transition: background 0.15s ease, opacity 0.15s ease;
}

.btn-review:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-review--approve {
    background: rgba(26, 127, 75, 0.1);
    color: #1a7f4b;
    border-color: rgba(26, 127, 75, 0.25);
}

.btn-review--approve:hover:not(:disabled) {
    background: rgba(26, 127, 75, 0.18);
}

.btn-review--reject {
    background: rgba(179, 38, 30, 0.06);
    color: #b3261e;
    border-color: rgba(179, 38, 30, 0.22);
}

.btn-review--reject:hover:not(:disabled) {
    background: rgba(179, 38, 30, 0.12);
}
</style>